import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FolderOpen, CheckCircle2, Send, FileText, Plus, ChevronRight, Loader2 } from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

const ESTADO_LABEL = {
    armando: { txt: 'Armando', color: '#3b82f6', bg: 'rgba(59,130,246,0.1)' },
    completo: { txt: 'Completo', color: '#10b981', bg: 'rgba(16,185,129,0.1)' },
    presentado: { txt: 'Presentado', color: '#8b5cf6', bg: 'rgba(139,92,246,0.1)' },
};

const Dashboard = () => {
    const { userProfile, getAuthHeaders } = useAuth();
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [expedientes, setExpedientes] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const headers = await getAuthHeaders();
                const [sRes, eRes] = await Promise.all([
                    fetch(`${API_URL}/api/expedientes-stats`, { headers }),
                    fetch(`${API_URL}/api/expedientes`, { headers }),
                ]);
                const sData = await sRes.json();
                const eData = await eRes.json();
                if (sData.ok) setStats(sData.stats);
                if (eData.ok) setExpedientes(eData.expedientes);
            } catch (e) { /* silent */ }
            finally { setLoading(false); }
        })();
    }, []);

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem', marginBottom: '2rem' }}>
                <div>
                    <h2 style={{ fontSize: '1.875rem', fontWeight: 'bold' }}>
                        Estación de Mando, {userProfile?.companyName || userProfile?.email?.split('@')[0]}
                    </h2>
                    <p style={{ color: 'hsl(var(--muted-foreground))' }}>Tus expedientes de participación y su estado de armado.</p>
                </div>
                <button onClick={() => navigate('/bases')} style={newBtn}>
                    <Plus size={18} /> Nuevo expediente
                </button>
            </div>

            {/* Stats */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
                <StatCard label="Expedientes en armado" value={stats?.enProceso ?? '—'} icon={FolderOpen} color="#3b82f6" />
                <StatCard label="Completos" value={stats?.completos ?? '—'} icon={CheckCircle2} color="#10b981" />
                <StatCard label="Presentados" value={stats?.presentados ?? '—'} icon={Send} color="#8b5cf6" />
                <StatCard label="Total" value={stats?.totalExpedientes ?? '—'} icon={FileText} color="#f59e0b" />
            </div>

            {/* Lista de expedientes */}
            <div className="card">
                <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1.5rem' }}>Mis Expedientes</h3>
                {loading ? (
                    <Center><Loader2 className="animate-spin" size={28} /></Center>
                ) : expedientes.length === 0 ? (
                    <div style={{ padding: '3rem', textAlign: 'center', color: 'hsl(var(--muted-foreground))', border: '1px dashed hsl(var(--border))', borderRadius: 'var(--radius)' }}>
                        Aún no tienes expedientes. Ve a <strong>"1. Bases y TDR"</strong>, sube las Bases Integradas y crea tu primer expediente.
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                        {expedientes.map(e => {
                            const est = ESTADO_LABEL[e.estado] || ESTADO_LABEL.armando;
                            return (
                                <div key={e.id} onClick={() => navigate(`/expediente/${e.id}`)}
                                    style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem', border: '1px solid hsl(var(--border))', borderRadius: 'var(--radius)', cursor: 'pointer', transition: 'background 0.15s' }}
                                    onMouseEnter={ev => ev.currentTarget.style.background = 'hsl(var(--muted))'}
                                    onMouseLeave={ev => ev.currentTarget.style.background = 'transparent'}>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontWeight: 600, fontSize: '0.9rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {e.proceso?.objeto || 'Expediente sin nombre'}
                                        </div>
                                        <div style={{ fontSize: '0.78rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.2rem' }}>
                                            {e.proceso?.entidad || '—'} · {e.proceso?.nomenclatura || ''}
                                        </div>
                                    </div>
                                    {/* mini progreso */}
                                    <div style={{ width: '120px', flexShrink: 0 }}>
                                        <div style={{ fontSize: '0.72rem', color: 'hsl(var(--muted-foreground))', marginBottom: '0.2rem', textAlign: 'right' }}>
                                            {e.resumen.cargados}/{e.resumen.total} · {e.resumen.progreso}%
                                        </div>
                                        <div style={{ height: '6px', background: 'hsl(var(--muted))', borderRadius: '999px', overflow: 'hidden' }}>
                                            <div style={{ width: `${e.resumen.progreso}%`, height: '100%', background: e.resumen.progreso === 100 ? '#10b981' : 'hsl(var(--primary))' }} />
                                        </div>
                                    </div>
                                    <span style={{ fontSize: '0.72rem', padding: '0.2rem 0.6rem', borderRadius: '999px', background: est.bg, color: est.color, fontWeight: 600, flexShrink: 0 }}>{est.txt}</span>
                                    <ChevronRight size={18} style={{ color: 'hsl(var(--muted-foreground))', flexShrink: 0 }} />
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

function StatCard({ label, value, icon: Icon, color }) {
    return (
        <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                    <p style={{ color: 'hsl(var(--muted-foreground))', fontSize: '0.875rem', marginBottom: '0.5rem' }}>{label}</p>
                    <h3 style={{ fontSize: '1.875rem', fontWeight: 'bold' }}>{value}</h3>
                </div>
                <div style={{ padding: '0.5rem', backgroundColor: color + '1a', color, borderRadius: 'var(--radius)' }}>
                    <Icon size={20} />
                </div>
            </div>
        </div>
    );
}

const Center = ({ children }) => <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>{children}</div>;
const newBtn = { display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.6rem 1.2rem', background: 'hsl(var(--primary))', color: 'white', border: 'none', borderRadius: 'var(--radius)', cursor: 'pointer', fontWeight: 600 };

export default Dashboard;
